import React, { ReactElement } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import StyledLoading from "../Loading/StyledLoading";
import { GET_WINE_SUMMARY } from "../../../graphql/resolvers/wine";
import { GetWineSummary } from "../../../types/__generated__/GetWineSummary";
import { isLoggedIn } from "../../../helpers/auth";
import {
  GlassIcon,
  GlobeIcon,
  GrapeInfoSection,
  GrapesIcon,
  RegionInfoSection,
  TasteSummaryTitle,
  TasteSummaryWrapper,
  WineCharacteristicsInfoSection,
  InfoTextWrapper,
} from "./style";

interface TasteSummaryProps {
  wineSlug: string | null | undefined;
  isWineDetailsPage: boolean;
}

const TasteSummary = ({ isWineDetailsPage, wineSlug }: TasteSummaryProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.MY_TASTE_PROFILE]);

  const { data: wineSummaryData } = useQuery<GetWineSummary>(GET_WINE_SUMMARY, {
    skip: !wineSlug,
    variables: { slug: wineSlug },
    partialRefetch: true,
  });

  const { wine } = wineSummaryData || {};
  const { wineSummary } = wine || {};
  const { tastingNote, region, grapeType } = wineSummary || {};
  const { ratingPosition: grapeRatingPosition } = grapeType || {};
  const { ratingPosition: regionRatingPosition } = region || {};
  const isTopThreeGrape: boolean = (grapeRatingPosition ?? 4) <= 3;
  const isTopThreeRegion: boolean = (regionRatingPosition ?? 4) <= 3;
  const isLoading = !wineSummaryData;

  const renderWineCharacteristicsInfoSection = (): ReactElement => (
    <WineCharacteristicsInfoSection>
      <GlassIcon isWineDetailsPage={isWineDetailsPage} />
      <InfoTextWrapper>
        <Trans
          i18nKey="myTasteProfile:tasteSummary.tastingNote"
          values={{
            body: tastingNote?.wineBody?.toLowerCase(),
            sweetness: tastingNote?.wineSweetness?.toLowerCase(),
            acidity: tastingNote?.wineAcidity?.toLowerCase(),
            fruitiness: tastingNote?.wineFruitDriven?.toLowerCase(),
            tannins: tastingNote?.wineTannin?.toLowerCase(),
            alcohol: tastingNote?.wineAlcoholPercentage?.toLowerCase(),
            oak: tastingNote?.wineOaky?.toLowerCase(),
          }}
          components={{ bold: <strong /> }}
        />
      </InfoTextWrapper>
    </WineCharacteristicsInfoSection>
  );

  const renderGrapeInfoSection = (): ReactElement => (
    <GrapeInfoSection>
      <GrapesIcon />
      <InfoTextWrapper>
        {isTopThreeGrape ? (
          <Trans
            i18nKey="myTasteProfile:tasteSummary.topGrapeInfo"
            values={{
              grapeName: grapeType?.name,
              amountStars: grapeType?.triedWinesAvgRating,
              interpolation: { escapeValue: false },
            }}
            components={{ bold: <strong /> }}
          />
        ) : (
          <Trans
            i18nKey="myTasteProfile:tasteSummary.grapeInfo"
            values={{
              amountWines: grapeType?.triedWinesCount,
              amountStars: grapeType?.triedWinesAvgRating,
              count: grapeType?.triedWinesCount,
            }}
            components={{ bold: <strong /> }}
          />
        )}
      </InfoTextWrapper>
    </GrapeInfoSection>
  );

  const renderRegionInfoSection = (): ReactElement => (
    <RegionInfoSection>
      <GlobeIcon />
      <InfoTextWrapper>
        {isTopThreeRegion ? (
          <Trans
            i18nKey="myTasteProfile:tasteSummary.topRegionInfo"
            values={{
              regionName: region?.name,
              amountStars: region?.triedWinesAvgRating,
              interpolation: { escapeValue: false },
            }}
            components={{ bold: <strong /> }}
          />
        ) : (
          <Trans
            i18nKey="myTasteProfile:tasteSummary.regionInfo"
            values={{
              amountWines: region?.triedWinesCount,
              amountStars: region?.triedWinesAvgRating,
              count: region?.triedWinesCount,
            }}
            components={{ bold: <strong /> }}
          />
        )}
      </InfoTextWrapper>
    </RegionInfoSection>
  );

  const isUserLoggedIn = isLoggedIn();
  const renderContent = (): ReactElement => (
    <TasteSummaryWrapper isWineDetailsPage={isWineDetailsPage}>
      <TasteSummaryTitle>{t("myTasteProfile:tasteSummary.title")}</TasteSummaryTitle>
      {renderWineCharacteristicsInfoSection()}
      {isUserLoggedIn && renderGrapeInfoSection()}
      {isUserLoggedIn && renderRegionInfoSection()}
    </TasteSummaryWrapper>
  );

  return isLoading ? <StyledLoading /> : renderContent();
};

export default TasteSummary;
